import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import { Heading, Subheading } from '../../../../components/Heading';
import { Card } from '../../../../components/Card';
import { Input } from '../../../../components/Input';
import { Button } from '../../../../components/Button';
import showLoadingToast from '../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import Loading from '../../../../components/Loading';
import { useAiReportCustomerProfiles } from '../../../../hooks/useAiReportCustomerProfile';
import { AiReportCustomerProfile } from '../../../../models/AiReportCustomerProfile';
import { CUSTOMER_PROFILE_FORM_FIELDS } from '../formFields';

const EditCustomerProfilePage = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [profile, setProfile] = useState<AiReportCustomerProfile | null>(null);
    const { updateProfile, fetchProfileById, loading, error } = useAiReportCustomerProfiles();

    useEffect(() => {
        const load = async () => {
            if (!id) return;
            try {
                const fetched = await fetchProfileById(Number(id));
                setProfile(fetched);
            } catch (err) {
                showErrorToast('Failed to fetch customer profile.');
            }
        };
        load();
    }, [id]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setProfile((prev) => (prev ? { ...prev, [name]: value } : null));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!profile) return;
        if (!profile.customer_name.trim()) {
            showErrorToast('Customer Name is required');
            return;
        }
        try {
            await updateProfile(profile.id, profile as any);
            showLoadingToast(`Updating ${profile.customer_name}...`, 100);
            setTimeout(() => {
                navigate('/gp/ai-report-helper/customer-profiles');
                showSuccessToast(`${profile.customer_name} updated successfully`);
            }, 1000);
        } catch (err) {
            showErrorToast('Failed to update customer profile.');
        }
    };

    if (loading) return <Loading />;
    if (error) return <p>{error}</p>;
    if (!profile) return <p>Customer profile not found.</p>;

    return (
        <form method="post" onSubmit={handleSubmit} className="mx-auto max-w-6xl">
            <div className="lg:flex lg:items-center lg:justify-between mb-5">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/gp/ai-report-helper/customer-profiles')}
                        className="text-zinc-400 hover:text-zinc-950 dark:hover:text-white"
                        aria-label="Back"
                    >
                        <ArrowLeftIcon className="size-5" />
                    </button>
                    <Heading>Edit Customer Profile</Heading>
                </div>
                <div className="mt-5 flex lg:ml-4 lg:mt-0">
                    <Button type="reset" outline onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                    <Button type="submit" className="ml-3">Save</Button>
                </div>
            </div>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Customer Profile</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            Constant details for this customer, used to auto-fill report columns
                            (e.g. District, State) that never appear in the source report.
                        </p>
                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        {CUSTOMER_PROFILE_FORM_FIELDS.map((field) => (
                            <div className="sm:col-span-full" key={field.name}>
                                <section>
                                    <div className="space-y-1">
                                        <Subheading>{field.label}</Subheading>
                                    </div>
                                    <div className="mt-2">
                                        <Input
                                            aria-label={field.label}
                                            name={field.name}
                                            value={(profile as any)[field.name] || ''}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </section>
                            </div>
                        ))}
                    </div>
                </div>
            </Card>

            <div className="flex justify-end gap-4 my-6">
                <Button type="reset" plain onClick={() => navigate(-1)}>
                    Cancel
                </Button>
                <Button type="submit">Save</Button>
            </div>
        </form>
    );
};

export default EditCustomerProfilePage;
